import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Grid from '@mui/material/Grid2';

import { DisablableOption, IStringHash, ModelBase } from "@models";
import { DataType, isDataType } from '@dndModels/Types';

import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import SelectStringBox from "@components/input/selects/SelectStringBox";
import LoadingBox from "@components/input/LoadingBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import AddButton from "@components/input/buttons/AddButton";

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

const EMPTY_OBJECT: Record<string, never> = {};
const EMPTY_ARRAY: never[] = [];

interface Props {
  data_type?: DataType | "Attached" | "";
  data_types?: (DataType | "Attached" | "")[];
  value?: string;
  values?: string[];
  filter?: any;
  filters?: any;
  extra_filter?: (o: ModelBase) => boolean;
  extras_from_fields?: Record<string, string[]>;
  options?: DisablableOption[];
  priority_extra_options?: (string | DisablableOption)[];
  extra_options?: (string | DisablableOption)[];
  allow_all?: boolean;
  allow_none?: boolean;
  allow_true_none?: boolean;
  multiple?: boolean;
  name: string;
  onChange?: Function | null; 
  onChangeWithDataTypes?: ((ids: { 
    id: string, data_type: string, 
  }[]) => void) | null;
  onChangeObject?: Function | null; 
  labelWidth?: number | null;
  color?: string;
  disabled?: boolean;
  display_format?: string;
  render_value_format?: string;
  display_format_backup?: string;
  limit?: number;
  log_it?: boolean;
  auto_sort?: boolean;
  close_on_extra_clicked?: boolean;
  ignore_missing?: boolean;
  images?: boolean;
  show_current_order?: boolean;
  auto_select_on_1?: boolean;
  max_selected?: number;
  reload_time?: number;
  sort?: (a: ModelBase, b: ModelBase) => number;
  use_field?: string;
  add_new?: boolean;
  dominant_only?: boolean;
  add_new_data?: { name?: string; description?: string };
  optionConvert?: (o: ModelBase) => DisablableOption;
}

export default function SelectObjectBox({
  name,
  data_type = "",
  data_types = EMPTY_ARRAY,
  value = "",
  values,
  color = "",
  options = EMPTY_ARRAY,
  priority_extra_options = EMPTY_ARRAY,
  extra_options = EMPTY_ARRAY,
  allow_all = false,
  allow_none = false,
  allow_true_none = false,
  filter = EMPTY_OBJECT,
  filters = EMPTY_OBJECT,
  extra_filter,
  extras_from_fields = EMPTY_OBJECT,
  disabled = false,
  display_format = "{obj.true_name}",
  render_value_format = "",
  display_format_backup = "{obj.name}",
  sort,
  limit = -1,
  onChange = null,
  onChangeWithDataTypes = null,
  onChangeObject = null,
  log_it = false,
  close_on_extra_clicked = false,
  ignore_missing = false,
  auto_sort = true,
  images = false,
  show_current_order = false,
  auto_select_on_1 = false,
  max_selected = -1,
  reload_time = 0,
  use_field = "_id",
  add_new = false,
  add_new_data,
  dominant_only = false,
  optionConvert
}: Props) {
  // ── Core data state ──────────────────────────────────────────────────────────
  const [objects, setObjects] = useState<ModelBase[]>([]);
  const [loading, setLoading] = useState(false);
  // extra options that may be augmented from extras_from_fields
  const [derived_extra_options, setDerivedExtraOptions] = useState<(string | DisablableOption)[]>(extra_options);
  // data_type_map: id → data_type string, used for onChangeWithDataTypes
  const [data_type_map, setDataTypeMap] = useState<IStringHash>({});

  // ── Multi-select controlled values ───────────────────────────────────────────
  const [my_values, setValues] = useState<string[]>(values ?? []);

  // ── reload_time sync ─────────────────────────────────────────────────────────
  const [my_reload_time, setReloadTime] = useState(reload_time);
  useEffect(() => {
    if (reload_time !== my_reload_time) {
      setValues(values ?? []);
      setReloadTime(reload_time);
    }
  }, [reload_time, my_reload_time, values]);

  // ── Add-new modal ─────────────────────────────────────────────────────────────
  const [new_object, setNewObject] = useState<ModelBase | null>(null);
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  // ── Stable callbacks via refs (avoids stale closures in event handlers) ───────
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onChangeWithDataTypesRef = useRef(onChangeWithDataTypes);
  onChangeWithDataTypesRef.current = onChangeWithDataTypes;
  const onChangeObjectRef = useRef(onChangeObject);
  onChangeObjectRef.current = onChangeObject;

  // ── Phase 1: Fetch objects whenever data_type / filter / options change ───────
  // Use a stringified key to avoid re-fetching on every render when callers
  // pass inline object literals (a common footgun).
  const filter_key = JSON.stringify(filter);
  const filters_key = JSON.stringify(filters);
  const data_types_key = JSON.stringify(data_types);

  useEffect(() => {
    // If caller supplied static options directly, no fetch needed
    if (options.length > 0) {
      setObjects([]);
      setDerivedExtraOptions(extra_options);
      setLoading(false);
      return;
    }

    if (data_type === "" && data_types.length === 0) {
      setObjects([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      if (log_it) {
        debugger;
      }
      try {
        if (data_types.length > 0 && data_types.every(t => isDataType(t))) {
          const res = await api.getSetOfObjects({
            data_types: data_types as DataType[],
            filter,
            filters: { ...filters },
            log_it,
          });

          if (cancelled) return;

          let fetched: ModelBase[] = [];
          let new_extra: (string | DisablableOption)[] = [...extra_options];
          const new_map: IStringHash = {};

          for (const type of data_types) {
            let typed = (res[type] ?? []);
            if (dominant_only) {
              typed = typed.filter(o => o.is_dominant);
            }
            fetched = [...fetched, ...typed];

            const fields = (extras_from_fields as any)[type] as string[] | undefined;
            if (fields) {
              for (const field of fields) {
                const seen = new Set<string>();
                for (const obj of typed) {
                  const v = (obj as any)[field] as string;
                  if (!seen.has(v)) {
                    seen.add(v);
                    new_map[v] = `${type} ${field}`;
                    new_extra.push(v);
                  }
                }
              }
            }
          }

          setObjects(fetched);
          setDerivedExtraOptions(new_extra);
          setDataTypeMap(prev => ({ ...prev, ...new_map }));
        } else if (data_type !== "" && data_type !== "Attached") {
          let res = await api.getObjects<ModelBase>({ data_type, filter: { ...filter }, log_it });
          if (cancelled) return;
          if (dominant_only) {
            res = res.filter(o => o.is_dominant);
          }
          setObjects(res);
          setDerivedExtraOptions(extra_options);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_type, data_types_key, filter_key, filters_key, options.length, dominant_only, log_it]);

  // Keep derived_extra_options in sync when extra_options prop changes
  // (only when we're not waiting on a fetch that sets them)
  useEffect(() => {
    if (options.length > 0 || (data_type === "" && data_types.length === 0)) {
      setDerivedExtraOptions(extra_options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra_options]);

  // ── Phase 2: Build SelectStringBox options from fetched objects ───────────────
  const string_limit = 50;

  const built_options = useMemo<DisablableOption[]>(() => {
    const new_map: IStringHash = {};
    const result: DisablableOption[] = [];

    // Priority extra options come first
    const priority: DisablableOption[] = priority_extra_options.map(o =>
      typeof o === "string"
        ? { id: o, text: o, disabled: false, reason: "" }
        : o
    );

    // Regular extra options come next
    const extras: DisablableOption[] = derived_extra_options.map(o =>
      typeof o === "string"
        ? { id: o, text: o, disabled: false, reason: "" }
        : o
    );

    // Mark extra option ids in the map
    [...priority, ...extras].forEach(o => {
      if (new_map[o.id] === undefined) new_map[o.id] = "extra_option";
    });

    // Static options passed directly
    let object_options: DisablableOption[] = [...(options.length > 0 ? options : [])];

    // Options built from fetched objects
    if (options.length === 0) {
      let filtered = objects;

      if (extra_filter) {
        filtered = filtered.filter(o => extra_filter(o));
      }

      if (sort) {
        filtered = [...filtered].sort(sort);
      } else if (auto_sort) {
        filtered = [...filtered].sort((a, b) =>
          a.use_name.trim().localeCompare(b.use_name.trim())
        );
      }

      for (const obj of filtered) {
        if (optionConvert) {
          object_options.push(optionConvert(obj));
        } else {
          const raw_text = char_util.getStringWithReplacementsWithObject({
            clean_me: display_format,
            backup: display_format_backup,
            obj,
          });
          const text = raw_text.length > string_limit
            ? raw_text.substring(0, string_limit) + "..."
            : raw_text;

          const raw_value_text = render_value_format
            ? char_util.getStringWithReplacementsWithObject({
                clean_me: render_value_format,
                backup: render_value_format,
                obj,
              })
            : "";
          const value_text = raw_value_text.length > string_limit
            ? raw_value_text.substring(0, string_limit) + "..."
            : raw_value_text;

          const id = (obj as any)[use_field] as string;
          new_map[id] = obj.data_type;
          object_options.push({
            id,
            text,
            value_text: value_text || undefined,
            image_url: obj.image_url,
            disabled: false,
            reason: "",
          });
        }
      }
    }

    if (auto_sort && !sort) {
      object_options = object_options.sort((a, b) => a.text.localeCompare(b.text));
      priority.sort((a, b) => a.text.localeCompare(b.text));
      extras.sort((a, b) => a.text.localeCompare(b.text));
    }

    result.push(...priority, ...extras, ...object_options);

    // Update data_type_map as a side effect — kept minimal to avoid setState-in-render
    // We do this via a deferred update below in a separate useEffect.
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, options, priority_extra_options, derived_extra_options, extra_filter,
      sort, auto_sort, display_format, display_format_backup, render_value_format,
      use_field, data_type, optionConvert]);

  // Sync data_type_map whenever built_options changes
  useEffect(() => {
    const new_map: IStringHash = {};
    for (const opt of built_options) {
      const obj = objects.find(o => (o as any)[use_field] === opt.id);
      new_map[opt.id] = obj ? obj.data_type : "extra_option";
    }
    setDataTypeMap(new_map);
  }, [built_options, objects, use_field]);

  // ── Auto-select when exactly 1 option ────────────────────────────────────────
  useEffect(() => {
    if (!auto_select_on_1 || built_options.length !== 1) return;
    const new_val = built_options[0].id;
    if (new_val === value) return;
    if (onChangeWithDataTypesRef.current) {
      onChangeWithDataTypesRef.current([{ id: new_val, data_type: data_type_map[new_val] ?? "" }]);
    } else if (onChangeRef.current) {
      onChangeRef.current(new_val);
    }
  }, [built_options, auto_select_on_1, value, data_type_map]);

  // ── Emitting changes ─────────────────────────────────────────────────────────
  const emit_single = useCallback((changed: string) => {
    if (onChangeWithDataTypesRef.current) {
      onChangeWithDataTypesRef.current([{ id: changed, data_type: data_type_map[changed] ?? "" }]);
    } else if (onChangeRef.current) {
      (onChangeRef.current as (v: string) => void)(changed);
    } else if (onChangeObjectRef.current) {
      if (extra_options.some(o => (typeof o === "string" ? o : o.id) === changed)) {
        onChangeObjectRef.current(changed);
        return;
      }
      const obj = objects.find(o => (o as any)[use_field] === changed);
      if (obj) onChangeObjectRef.current(obj);
    }
  }, [data_type_map, extra_options, objects, use_field]);

  const emit_multi = useCallback((changed: string[]) => {
    if (onChangeWithDataTypesRef.current) {
      onChangeWithDataTypesRef.current(
        changed.map(id => ({ id, data_type: data_type_map[id] ?? "" }))
      );
    } else if (onChangeRef.current) {
      (onChangeRef.current as (v: string[]) => void)(changed);
    } else if (onChangeObjectRef.current) {
      onChangeObjectRef.current(
        objects.filter(o => changed.includes((o as any)[use_field]))
      );
    }
  }, [data_type_map, objects, use_field]);

  // ── Add-new helpers ───────────────────────────────────────────────────────────
  const make_new_object = useCallback((): ModelBase | null => {
    if (!isDataType(data_type)) return null;
    const newbie = new ModelBase();
    newbie.name = add_new_data?.name ?? `New ${data_type}`;
    newbie.description = add_new_data?.description ?? `New ${data_type}`;
    newbie.data_type = data_type;
    if (api.user?.admin) api.applyWorkingOn(newbie);
    return newbie;
  }, [data_type, add_new_data]);

  const open_add_new = useCallback(() => {
    const obj = make_new_object();
    if (!obj) return;
    setNewObject(obj);
    setOpen(true);
  }, [make_new_object]);

  const submit_new_object = useCallback(() => {
    if (!data_type || data_type === "Attached" || !new_object || !api.user) return;
    const canEdit =
      api.user.admin ||
      new_object._id === "" ||
      new_object.owner_id === api.user._id;
    if (!canEdit) return;

    setProcessing(true);
    if (!api.user.admin || new_object.source_books_old.some(o => o.id === "Personal")) {
      new_object.owner_id = api.user._id;
    } else if (api.user.admin && !new_object.source_books_old.some(o => o.type === "Homebrew")) {
      new_object.owner_id = "";
    }

    api.upsertObject(data_type, new_object, true).then(res => {
      if (res?.res instanceof ModelBase) {
        const saved = res.res;
        setObjects(prev => [...prev, saved]);
        if (values !== undefined) {
          const next = [...my_values, saved._id];
          setValues(next);
          emit_multi(next);
        } else {
          emit_single(saved._id);
        }
      }
      setProcessing(false);
      setOpen(false);
      setReloadTime(common_util.get_time_number());
    });
  }, [data_type, new_object, values, my_values, emit_single, emit_multi]);

  // ── Extra options for SelectStringBox ────────────────────────────────────────
  const select_extra_options = useMemo<(string | DisablableOption)[]>(() => {
    const out: (string | DisablableOption)[] = [];
    if (allow_all) out.push({ id: "All", text: "All" });
    if (allow_none) out.push({ id: "None", text: "None" });
    else if (allow_true_none) out.push({ id: "True None", text: "None" });
    return out;
  }, [allow_all, allow_none, allow_true_none]);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (
    data_type === "" &&
    data_types.length === 0 &&
    options.length === 0 &&
    extra_options.length === 0
  ) {
    return <span />;
  }

  if (loading) {
    return <LoadingBox />;
  }

  const add_new_button = add_new && (
    <Grid
      size={{
        xs: 12
      }}>
      <AddButton label="Add New" disabled={disabled} onClick={open_add_new} />
    </Grid>
  );

  const add_new_modal = new_object && (
    <ModalOrDrawer open={open} onClose={() => setOpen(false)}>
      <ModelBaseInput
        obj={new_object}
        disabled={false}
        change_time={common_util.get_time_number()}
        no_back_button
        processing={processing}
        onSubmit={submit_new_object}
        onCancel={() => setOpen(false)}
        onChange={(changed: ModelBase) => {
          setNewObject(prev => {
            if (!prev) return prev;
            const copy = new ModelBase();
            copy.copy(changed);
            return copy;
          });
        }}
      />
    </ModalOrDrawer>
  );

  if (values !== undefined) {
    // ── Multi-select mode ───────────────────────────────────────────────────────
    return (
      <Grid container spacing={0} direction="row">
        {add_new_button}
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name={name}
            options={built_options}
            extra_options={select_extra_options}
            disabled={disabled}
            values={my_values}
            max_selected={max_selected}
            close_on_extra_clicked={close_on_extra_clicked}
            ignore_missing={ignore_missing}
            reload_time={my_reload_time}
            images={images}
            show_current_order={show_current_order}
            color={color}
            onChange={(changed: string[]) => {
              // Handle special sentinel values
              if (changed.includes("All")) {
                const all = built_options.map(o => o.id);
                setValues(all);
                setReloadTime(common_util.get_time_number());
                return;
              }
              if (changed.includes("None") || changed.includes("True None")) {
                setValues([]);
                setReloadTime(common_util.get_time_number());
                return;
              }
              if (changed.includes("Remove Orphaned Values")) {
                const valid = changed.filter(id => built_options.some(o => o.id === id));
                setValues(valid);
                setReloadTime(common_util.get_time_number());
                return;
              }
              setValues(changed);
            }}
            onClose={(changed: string[]) => {
              if (changed.includes("Add New")) {
                open_add_new();
                return;
              }
              emit_multi(changed);
              setValues(changed);
            }}
          />
        </Grid>
        {add_new_modal}
      </Grid>
    );
  }

  // ── Single-select mode ────────────────────────────────────────────────────────
  if (auto_select_on_1 && built_options.length === 1) {
    return <span>{built_options[0].text}</span>;
  }

  return (
    <Grid container spacing={0} direction="row">
      {add_new_button}
      <Grid size={12}>
        <SelectStringBox
          name={name}
          options={built_options}
          extra_options={select_extra_options}
          disabled={disabled}
          value={value}
          images={images}
          color={color}
          onChange={(changed: string) => emit_single(changed)}
        />
      </Grid>
      {add_new_modal}
    </Grid>
  );
}